/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AiSuggestionDialogContainer.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { useEffect, useRef, useState } from "react";
import CustomDialogBoxV2 from "../common/customDialogBox/CustomDialogBoxV2";
import AISuggestionContent from "./AiSuggestionContext";
import { useDispatch } from "react-redux";
import AISuggestionHeader from "./AiSuggestionHeader";
import smartMatchActions from "../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import helper from "../../../utils/helper";
import AISuggestionWhyDrawer from "./Suggestion-Reasoning/AiSuggestionWhyDrawer";
import { Box, Typography } from "@mui/material";
import CustomButtonV2 from "../common/button/CustomButtonV2";
import CustomToolTip from "../../infoButton/CustomToolTip";
import { toast } from "react-toastify";
import PropTypes from "prop-types";


export default function AISuggestionDialogContainer({
  open,
  onClose,
  entity,
  onLinkRequest,
  onUnlinkRequest,
  closeDialogOnAdd = false,
}) {
  const [status, setStatus] = useState("loading"); // idle | loading | success | empty | error
  const [data, setData] = useState([]);
  const dispatch = useDispatch();
  const [apiParams, setApiParams] = useState(null);
  const [dialogTitle, setDialogTitle] = useState("AI Suggestions");
  const [whyOpen, setWhyOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [showQueueDialog, setShowQueueDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalSuggestionsCount, setTotalSuggestionsCount] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(
    "Preparing AI suggestions...",
  );
  const [loadingHistory, setLoadingHistory] = useState([]);
  const hasAutoTriggeredDeepSearchRef = useRef(false);
  const hasShownDeepEmptyToastRef = useRef(false);
  const isDeepSearchActive = Boolean(apiParams?.deepSearch);
  const requestAbortRef = useRef(null);
  const pendingFinalPayloadRef = useRef(null);
  const loadingMessageQueueRef = useRef([]);
  const loadingMessagePumpRef = useRef(null);

  const clearLoadingMessagePump = () => {
    loadingMessageQueueRef.current = [];
    pendingFinalPayloadRef.current = null;
    if (loadingMessagePumpRef.current) {
      clearTimeout(loadingMessagePumpRef.current);
      loadingMessagePumpRef.current = null;
    }
  };

  const enqueueLoadingMessage = (msg) => {
    if (!msg) return;
    const queue = loadingMessageQueueRef.current;
    const lastQueued = queue.length ? queue[queue.length - 1] : null;
    if (lastQueued === msg) return;
    queue.push(msg);

    if (loadingMessagePumpRef.current) return;

    const pump = () => {
      const next = loadingMessageQueueRef.current.shift();
      if (next) {
        setLoadingMessage(next);
        setLoadingHistory((prev) => [...prev, next]);
        loadingMessagePumpRef.current = setTimeout(pump, 500);
      } else {
        loadingMessagePumpRef.current = null;
        if (pendingFinalPayloadRef.current) {
          const finalPayload = pendingFinalPayloadRef.current;
          pendingFinalPayloadRef.current = null;
          handleFinalSuggestionResponse(finalPayload);
        }
      }
    };

    pump();
  };

  const openWhyThis = (recommendation) => {
    setSelectedRecommendation(recommendation);
    console.log("Selected Recommendation for Why This:", recommendation);
    setWhyOpen(true);
  };

  const closeWhyThis = () => {
    setWhyOpen(false);
    setSelectedRecommendation(null);
  };

  /**
   * Fetch AI Suggestions - via API call
   */
  const handleFinalSuggestionResponse = (resp) => {
    console.log("API Response:", resp);
    if (resp?.error) {
      setData([]);
      setTotalSuggestionsCount(0);
      setStatus("error");
    } else if (resp?.queue === true) {
      setShowQueueDialog(true);
      setStatus("idle");
    } else if (!resp?.recommendations || resp.recommendations.length === 0) {
      setTotalSuggestionsCount(
        resp?.totalCount ?? resp?.metadata?.totalCount ?? 0,
      );
      setData([]);
      setStatus("empty");
    } else {
      setTotalSuggestionsCount(
        resp?.totalCount ??
          resp?.metadata?.totalCount ??
          resp?.recommendations?.length ??
          0,
      );
      setData(resp);
      setStatus("success");
    }
  };

  const parseSseChunkLines = (chunkText, onEvent) => {
    const lines = chunkText.split("\n");
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) return;
      const payload = trimmed.slice(5).trim();
      if (!payload) return;
      try {
        const evt = JSON.parse(payload);
        onEvent(evt);
      } catch (err) {
        console.warn("Failed parsing SSE event payload:", payload, err);
      }
    });
  };

  const fetchSuggestions = async () => {
    const baseURL = process.env.REACT_APP_BASE_URL || "";
    const endpoint = smartMatchActions?.get_suggestions?.api_name;
    const url = endpoint?.startsWith("http")
      ? endpoint
      : `${baseURL}${endpoint}`;
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token") || "";

    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
    }
    const controller = new AbortController();
    requestAbortRef.current = controller;
    clearLoadingMessagePump();
    setLoadingHistory([]);
    setLoadingMessage("Starting recommendation pipeline...");

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ...apiParams }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Recommendation API failed with status ${response.status}`,
        );
      }

      const contentType = response.headers.get("content-type") || "";
      const isSse = contentType.includes("text/event-stream");

      if (isSse && response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let finalPayload = null;
        let buffered = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffered += decoder.decode(value, { stream: true });
          const parts = buffered.split("\n\n");
          buffered = parts.pop() || "";

          parts.forEach((part) => {
            parseSseChunkLines(part, (evt) => {
              const msg = `${evt?.message || "Processing..."}`;
              enqueueLoadingMessage(msg);

              if (
                evt?.stage === "FINAL_RESULT" &&
                evt?.status === "COMPLETED"
              ) {
                finalPayload = evt?.payload || null;
              }
            });
          });
        }

        if (buffered) {
          parseSseChunkLines(buffered, (evt) => {
            const stageLabel = evt?.stage ? `[${evt.stage}] ` : "";
            const msg = `${stageLabel}${evt?.message || "Processing..."}`;
            enqueueLoadingMessage(msg);
            if (evt?.stage === "FINAL_RESULT" && evt?.status === "COMPLETED") {
              finalPayload = evt?.payload || null;
            }
          });
        }

        if (finalPayload) {
          pendingFinalPayloadRef.current = finalPayload;
          if (
            !loadingMessagePumpRef.current &&
            loadingMessageQueueRef.current.length === 0
          ) {
            const readyPayload = pendingFinalPayloadRef.current;
            pendingFinalPayloadRef.current = null;
            handleFinalSuggestionResponse(readyPayload);
          }
          return;
        }
      }

      // Non-stream fallback: supports old response and event-wrapper response.
      const json = await response.json();
      if (json?.stage === "FINAL_RESULT" && json?.payload) {
        handleFinalSuggestionResponse(json.payload);
      } else {
        handleFinalSuggestionResponse(json);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("fetchSuggestions error:", err);
      setData([]);
      setTotalSuggestionsCount(0);
      setStatus("error");
    }
  };

  /**
   * Use Effect to update API params when entity changes
   */

  useEffect(() => {
    if (!entity) {
      setApiParams(null);
      setStatus("error");
      return;
    }
    if (!Array.isArray(entity.source) || entity.source.length === 0) {
      console.error("Smart Match requires at least one source entity.");
      setApiParams(null);
      setStatus("error");
      return;
    }
    hasAutoTriggeredDeepSearchRef.current = false;
    hasShownDeepEmptyToastRef.current = false;
    clearLoadingMessagePump();
    setStatus("loading");
    setPage(1);
    setLoadingHistory([]);
    setLoadingMessage("Preparing request...");
    console.log("Entity changed:", entity);
    const source = entity.source;

    setApiParams({
      source,
      intent: "SMART_MATCH",
      scope: entity?.scope || null,
      filters: {
        targetEntityTypes: entity?.targetEntityTypes || [source[0].entityType],
        excludeExistingLinks: true,
      },
      options: {
        // topK: entity?.topK || 5,
        topK: 50,
        minConfidence: entity?.minConfidence || 0.1,
        explain: true,
      },
      pageNumber: 1,
      pageSize: rowsPerPage,
      check_queue: true,
    });
    //Setting dialog title
    console.log("entity Description", entity?.description);
    const titleSuffix = entity?.description
      ? ` for ${helper.toSentenceCase(entity.description)}`
      : "";

    setDialogTitle(`AI Suggestions ${titleSuffix}`);
  }, [entity, rowsPerPage]);

  /**
   * Use Effect to make API call when apiParams change
   */
  useEffect(() => {
    if (apiParams) {
      fetchSuggestions();
    }
  }, [apiParams]);

  useEffect(() => {
    if (
      status === "empty" &&
      apiParams &&
      !apiParams?.deepSearch &&
      !hasAutoTriggeredDeepSearchRef.current
    ) {
      hasAutoTriggeredDeepSearchRef.current = true;
      toast.success("No suggestions found in fast mode. Trying deep analysis.");
      const deepSearchTimer = setTimeout(() => {
        handleDeepAnalysis();
      }, 3000);

      return () => clearTimeout(deepSearchTimer);
    }
  }, [status, apiParams]);

  useEffect(() => {
    if (
      status === "empty" &&
      apiParams?.deepSearch &&
      !hasShownDeepEmptyToastRef.current
    ) {
      hasShownDeepEmptyToastRef.current = true;
      toast.info("No suggestion found in deep analysis.");
    }
  }, [status, apiParams]);

  useEffect(() => {
    console.log("Dialog Title:", dialogTitle);
  }, [dialogTitle]);

  useEffect(() => {
    return () => {
      if (requestAbortRef.current) {
        requestAbortRef.current.abort();
        requestAbortRef.current = null;
      }
      clearLoadingMessagePump();
    };
  }, []);

  const handleDialogClose = () => {
    dispatch({
      type: smartMatchActions.clear_suggestions.action_type,
    });

    setData([]);
    setStatus("loading");
    setApiParams(null);
    setShowQueueDialog(false);
    setPage(1);
    setTotalSuggestionsCount(0);
    hasAutoTriggeredDeepSearchRef.current = false;
    hasShownDeepEmptyToastRef.current = false;
    clearLoadingMessagePump();
    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
      requestAbortRef.current = null;
    }

    onClose();
  };

  const handleQueueDialogYes = () => {
    setShowQueueDialog(false);
    setStatus("loading");
    const updatedParams = { ...apiParams, check_queue: false };
    setApiParams(updatedParams);
  };

  const handleQueueDialogNo = () => {
    setShowQueueDialog(false);
    handleDialogClose();
  };

  const handleDeepAnalysis = () => {
    if (!apiParams) return;
    setStatus("loading");
    setPage(1);
    const updatedParams = {
      ...apiParams,
      deepSearch: true,
      pageNumber: 1,
      pageSize: rowsPerPage,
    };
    setApiParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setStatus("loading");
    setApiParams((prev) => ({
      ...prev,
      pageNumber: newPage,
      pageSize: rowsPerPage,
      check_queue: false,
    }));
  };

  return (
    <>
      <CustomDialogBoxV2
        open={open && !showQueueDialog}
        onClose={handleDialogClose}
        dialogRadius="0rem"
        headerSlot={
          <AISuggestionHeader
            onClose={onClose}
            title={<CustomToolTip length={70}>{dialogTitle}</CustomToolTip>}
          />
        }
        hideDefaultActions={status === "loading"}
        requiresDefaultPaddingForContent={false}
        dialogActions={
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <CustomButtonV2
              size="small"
              type="other"
              btnName="Deep Analysis"
              handleOnClick={handleDeepAnalysis}
              disabled={isDeepSearchActive}
              btnStyle={{
                backgroundColor: "#0F81AF",
                color: "#FFFFFF",
                border: "none",
                // height: "32px",
                // minWidth: "120px",
                fontSize: "14px",
                fontWeight: 400,
                "&:hover": {
                  backgroundColor: "#0C6F97",
                  border: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#7FB8CF",
                  color: "#FFFFFF",
                  border: "none",
                },
              }}
            />
            <CustomButtonV2
              size="small"
              type="cancel"
              btnName="Close"
              handleOnClick={() => {
                handleDialogClose();
              }}
            />
          </Box>
        }
      >
        <AISuggestionContent
          status={status}
          data={data}
          loadingMessage={loadingMessage}
          loadingHistory={loadingHistory}
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalSuggestionsCount}
          onPageChange={handlePageChange}
          onWhyThis={openWhyThis}
          onLinkRequest={onLinkRequest}
          onUnlinkRequest={onUnlinkRequest}
          onClose={onClose}
          closeDialogOnAdd={closeDialogOnAdd}
        />
      </CustomDialogBoxV2>

      {/*  Drawer is mounted HERE */}
      <AISuggestionWhyDrawer
        open={whyOpen}
        onClose={closeWhyThis}
        recommendation={selectedRecommendation}
      />

      {/* Queue Dialog */}
      <CustomDialogBoxV2
        open={showQueueDialog}
        onClose={handleQueueDialogNo}
        title="Knowledge Graph"
        actionButtonStyle={"continuous"}
        successButtonName={"Yes"}
        rejectButtonName={"No"}
        onClickSuccess={handleQueueDialogYes}
        onClickReject={handleQueueDialogNo}
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Knowledge Graph build in progress. Do you want to see existing
            suggestions?
          </Typography>
        </Box>
      </CustomDialogBoxV2>
    </>
  );
}

AISuggestionDialogContainer.propTypes = {
  open: PropTypes.bool.isRequired,
  entity: PropTypes.shape({
    source: PropTypes.arrayOf(
      PropTypes.shape({
        entityType: PropTypes.string.isRequired,
        entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      }),
    ).isRequired,
    description: PropTypes.string,
    scope: PropTypes.string,
    targetEntityTypes: PropTypes.arrayOf(PropTypes.string),
    topK: PropTypes.number,
    minConfidence: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onLinkRequest: PropTypes.func.isRequired,
  onUnlinkRequest: PropTypes.func.isRequired,
  closeDialogOnAdd: PropTypes.bool,
};
